// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// Small SVG helpers shared by the hand-rolled charts. No charting library:
// every mark on this site is an element this code created, so every mark can
// carry a tooltip, a click handler and an accessible name.

import { TENURE, TENURE_ORDER, UNRECORDED, type Tenure } from '../theme';

const NS = 'http://www.w3.org/2000/svg';

export function el<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number | null | undefined> = {},
  ...children: (Node | string)[]
): SVGElementTagNameMap[K] {
  const node = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (v == null) continue;
    node.setAttribute(k, String(v));
  }
  for (const c of children) node.append(c);
  return node;
}

/**
 * A responsive chart root.
 *
 * `width: 100%` with a viewBox is the trap that scales every text node with
 * the box — labels end up 1.9x on a wide screen and 0.84x on a narrow one. So
 * the SVG gets an explicit pixel width from its container and only the height
 * is derived; text then renders at the size it was authored at.
 */
export function chart(width: number, height: number, label: string): SVGSVGElement {
  const svg = el('svg', {
    class: 'chart',
    viewBox: `0 0 ${width} ${height}`,
    width,
    height,
    role: 'group',
    'aria-label': label,
    // Not role="img": that makes the SVG an accessibility LEAF and prunes
    // every focusable mark inside it from the tree while leaving them in the
    // tab order, so a screen-reader user tabs into things that announce
    // nothing.
  });
  return svg;
}

/** The width a chart should draw at, given its container. */
export function measure(host: HTMLElement, fallback = 900): number {
  const w = host.getBoundingClientRect().width;
  return w > 40 ? Math.floor(w) : fallback;
}

/**
 * THE TEXTURES.
 *
 * Colour is never load-bearing on this site: every outcome category is a
 * (hue, texture) pair, so all four separate in greyscale and under any form of
 * colour blindness. These patterns are the texture half. They are defined once
 * per SVG and referenced by fill.
 */
export function defs(svg: SVGSVGElement): SVGDefsElement {
  const existing = svg.querySelector('defs');
  if (existing) return existing as SVGDefsElement;
  const d = el('defs');

  for (const t of TENURE_ORDER) {
    const style = TENURE[t];
    if (!style.texture) continue;
    const p = el('pattern', {
      id: `tex-${t}`,
      patternUnits: 'userSpaceOnUse',
      width: 6,
      height: 6,
      patternTransform: style.texture === 'hatch' ? 'rotate(45)' : null,
    });
    p.append(el('rect', { width: 6, height: 6, fill: style.fill }));
    if (style.texture === 'stipple') {
      p.append(el('circle', { cx: 1.5, cy: 1.5, r: 1, fill: 'rgba(255,255,255,.62)' }));
      p.append(el('circle', { cx: 4.5, cy: 4.5, r: 1, fill: 'rgba(255,255,255,.62)' }));
    } else {
      p.append(el('rect', { x: 0, y: 0, width: 2.2, height: 6, fill: 'rgba(255,255,255,.55)' }));
    }
    d.append(p);
  }

  const unrec = el('pattern', {
    id: 'tex-unrecorded', patternUnits: 'userSpaceOnUse', width: 6, height: 6, patternTransform: 'rotate(45)',
  });
  unrec.append(el('rect', { width: 6, height: 6, fill: '#efeae0' }));
  unrec.append(el('rect', { width: 2, height: 6, fill: UNRECORDED }));
  d.append(unrec);

  svg.prepend(d);
  return d;
}

/** The paint for a tenure class — the pattern where it has one, the flat hue otherwise. */
export function tenureFill(t: Tenure): string {
  return TENURE[t].texture ? `url(#tex-${t})` : TENURE[t].fill;
}

/** The flat hue, for legend swatches and anywhere a pattern would be too small to read. */
export function tenureHue(t: Tenure): string {
  return TENURE[t].fill;
}

export const UNRECORDED_FILL = 'url(#tex-unrecorded)';

/** Nice round axis ticks that always include the top of the range. */
export function ticks(max: number, count = 5): number[] {
  if (!(max > 0)) return [0];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? mag * 10;
  const out: number[] = [];
  for (let v = 0; v <= max + step * 0.001; v += step) out.push(Number(v.toFixed(10)));
  return out;
}

/** A horizontal legend built from the four outcome categories, in the fixed order. */
export function tenureLegend(opts: {
  onToggle?: (t: Tenure, on: boolean) => void;
  active?: Set<Tenure>;
} = {}): HTMLElement {
  const wrap = document.createElement('div');
  wrap.className = 'legend';
  for (const t of TENURE_ORDER) {
    const style = TENURE[t];
    const on = opts.active ? opts.active.has(t) : true;
    const item = document.createElement(opts.onToggle ? 'button' : 'span');
    item.className = 'legend-item';
    if (opts.onToggle) {
      (item as HTMLButtonElement).type = 'button';
      item.setAttribute('aria-pressed', String(on));
      item.addEventListener('click', () => {
        const next = item.getAttribute('aria-pressed') !== 'true';
        item.setAttribute('aria-pressed', String(next));
        opts.onToggle!(t, next);
      });
    }
    const sw = document.createElement('span');
    sw.className = 'legend-swatch';
    sw.style.background = style.fill;
    if (style.texture === 'stipple') sw.style.backgroundImage = 'radial-gradient(rgba(255,255,255,.75) 1px, transparent 1px)';
    if (style.texture === 'stipple') sw.style.backgroundSize = '4px 4px';
    if (style.texture === 'hatch') sw.style.backgroundImage = 'repeating-linear-gradient(45deg, rgba(255,255,255,.7) 0 2px, transparent 2px 4px)';
    const label = document.createElement('span');
    label.textContent = style.label;
    item.append(sw, label);
    wrap.append(item);
  }
  return wrap;
}
